import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import API_CONFIG from '../config/api';
import { apiFetch, getToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import PainelDecisaoPagamentoModal from '../components/notasFiscais/PainelDecisaoPagamentoModal';
import { withSetorScope, isFiscalSetorial, getFiscalSetor } from '../utils/dataIsolation';

const STATUS_LABELS = {
  PENDENTE: 'Pendente',
  EM_ANDAMENTO: 'Em andamento',
  AUTORIZADO: 'Autorizado',
  NEGADO: 'Negado'
};

const STATUS_COLORS = {
  PENDENTE: { color: '#ef6c00', background: 'rgba(239,108,0,0.12)' },
  EM_ANDAMENTO: { color: '#1565c0', background: 'rgba(21,101,192,0.12)' },
  AUTORIZADO: { color: '#2e7d32', background: 'rgba(46,125,50,0.12)' },
  NEGADO: { color: '#c62828', background: 'rgba(198,40,40,0.12)' }
};

const normalizarChave = (valor) => {
  if (!valor || typeof valor !== 'string') return '';
  return valor
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
};

export default function ProcessoPagamentoPage() {
  const navigate = useNavigate();
  const { user, isAdmin, isFiscal, fiscalSetor, unidadeSelecionada, rbacRole } = useAuth();
  const usuarioSetor = user?.setor || '';
  const setorCodigo = normalizarChave(usuarioSetor);
  const podeEnviarParaFinanceiro = isAdmin || setorCodigo === 'SETOR_DE_CONTRATOS' || setorCodigo === 'SETOR_CONTRATOS';
  const scopeOpts = { user, rbacRole, fiscalSetor };

  const [atividades, setAtividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFiltro, setStatusFiltro] = useState('TODOS');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [modalOpen, setModalOpen] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviandoFinanceiroId, setEnviandoFinanceiroId] = useState(null);

  const podeDecidir = isFiscal && !!fiscalSetor && !isAdmin;

  const loadAtividades = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFiltro !== 'TODOS') params.set('status', statusFiltro);
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const setorScope = getFiscalSetor(user, fiscalSetor);
      if (isFiscalSetorial(user, rbacRole) && setorScope) {
        params.set('setor', setorScope);
      }
      const path = withSetorScope(
        `/api/notas-fiscais/atividades-processo-pagamento${params.toString() ? `?${params.toString()}` : ''}`,
        scopeOpts
      );
      const res = await apiFetch(path);
      const data = res.ok ? await res.json() : [];
      const lista = Array.isArray(data) ? data : [];
      const normalizado = lista.map((row) => ({
        tipo: 'PROCESSO_PAGAMENTO',
        id: row.id,
        processoId: `proc_pag_${row.id}`,
        processoCodigo: row.numero_processo,
        titulo: `Processo de Pagamento - ${row.numero_processo}`,
        status: row.status,
        statusLabel: STATUS_LABELS[row.status] || row.status,
        criadoEm: row.criado_em,
        notaFiscalIdPrincipal: row.nota_fiscal_id_principal,
        numeroProcesso: row.numero_processo,
        empresa: row.empresa,
        setor: row.setor,
        destinoLabel: 'Fiscal',
        signatarios: row.signatarios || [],
        observacao: row.observacao || null,
        observacoesHistorico: row.observacoesHistorico || [],
        assinaturaDocumentoId: row.assinaturaDocumentoId || null,
        avaliacaoAssinaturaDocumentoId: row.avaliacaoAssinaturaDocumentoId || null,
        etapaAtual: row.etapaAtual ?? row.etapa_atual ?? 0,
        fluxoId: row.fluxoId ?? row.fluxo_id ?? null,
        numeroContrato: row.numeroContrato || null,
        contratoPath: row.contratoPath || null,
        contratoEmpresa: row.contratoEmpresa || row.empresa,
      }));
      setAtividades(normalizado);
    } catch (e) {
      console.error(e);
      setAtividades([]);
      setSnackbar({ open: true, message: 'Erro ao carregar processos.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [statusFiltro, unidadeSelecionada?.codigo, user, rbacRole, fiscalSetor]);

  useEffect(() => {
    loadAtividades();
  }, [loadAtividades]);

  const abrirModal = (atv) => {
    setSelecionado(atv);
    setModalOpen(true);
    setPdfBlobUrl(null);
  };

  const fecharModal = () => {
    setModalOpen(false);
    setSelecionado(null);
    setPdfBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  useEffect(() => {
    if (!selecionado?.notaFiscalIdPrincipal || !modalOpen) return;
    const token = getToken();
    if (!token) return;
    setLoadingPdf(true);
    fetch(`${API_CONFIG.BACKEND_URL}/api/notas-fiscais/${selecionado.notaFiscalIdPrincipal}/pdf-consolidado`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) setPdfBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
      })
      .finally(() => setLoadingPdf(false));
  }, [selecionado?.notaFiscalIdPrincipal, modalOpen]);

  const enviarDecisao = async ({ acao, observacao, etapaDestinoIndex, usuarioEmail }) => {
    const atv = selecionado;
    if (!atv || !podeDecidir) return;
    const podeDecidirEste = atv.setor === fiscalSetor;
    if (!podeDecidirEste) {
      setSnackbar({ open: true, message: 'Apenas o fiscal do setor pode decidir.', severity: 'warning' });
      return;
    }
    const obs = String(observacao || '').trim();
    if (!obs) {
      setSnackbar({ open: true, message: 'Informe a observação para registrar a auditoria.', severity: 'warning' });
      return;
    }
    setEnviando(true);
    try {
      const res = await apiFetch(`/api/notas-fiscais/atividades/${atv.id}/decisao`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          acao,
          usuarioEmail: usuarioEmail || user?.email,
          observacao: obs,
          etapaDestinoIndex,
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao registrar decisão');
      setAtividades((prev) => prev.map((a) => (a.id === atv.id ? {
        ...a,
        status: data.status || a.status,
        etapaAtual: data.etapaAtual ?? a.etapaAtual,
      } : a)));
      setSnackbar({ open: true, message: data.message || 'Decisão registrada.', severity: 'success' });
      fecharModal();
      loadAtividades();
      if (acao === 'AUTORIZAR') {
        const notaId = data.notaFiscalId || atv.notaFiscalIdPrincipal;
        const pagDocId = data.assinaturaDocumentoId || atv.assinaturaDocumentoId;
        const avalDocId = data.avaliacaoAssinaturaDocumentoId || atv.avaliacaoAssinaturaDocumentoId;
        if (data.precisaAvaliacao !== false) {
          navigate('/notas-fiscais', {
            state: {
              avaliarNotaId: notaId,
              pagamentoAssinaturaDocumentoId: pagDocId,
              avaliacaoAssinaturaDocumentoId: avalDocId,
            },
          });
        } else if (avalDocId) {
          navigate(`/assinaturas/documentos/${avalDocId}`, {
            state: { proximoAssinaturaDocumentoId: pagDocId },
          });
        } else if (pagDocId) {
          navigate(`/assinaturas/documentos/${pagDocId}`);
        } else {
          navigate('/notas-fiscais', { state: { avaliarNotaId: notaId } });
        }
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Erro ao registrar decisão', severity: 'error' });
    } finally {
      setEnviando(false);
    }
  };

  const podeDecidirEste = selecionado && podeDecidir && selecionado.setor === fiscalSetor && selecionado.status === 'PENDENTE';

  const handleEnviarParaFinanceiro = async (notaId, e) => {
    if (e) e.stopPropagation();
    if (!notaId || !podeEnviarParaFinanceiro) return;
    if (!window.confirm('Tem certeza que deseja enviar este processo para o financeiro? Será gerado um PDF consolidado com todos os documentos.')) return;
    setEnviandoFinanceiroId(notaId);
    try {
      const token = getToken();
      const res = await fetch(`${API_CONFIG.BACKEND_URL}/api/notas-fiscais/${notaId}/enviar-financeiro`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao enviar para o financeiro');
      setSnackbar({ open: true, message: data?.message || 'Enviado para o financeiro com sucesso.', severity: 'success' });
      loadAtividades();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Erro ao enviar para o financeiro', severity: 'error' });
    } finally {
      setEnviandoFinanceiroId(null);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <VisaoRestritaBanner />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} color="primary">
          Atividades de Notas Fiscais
        </Typography>
        <Stack direction="row" gap={1}>
          <Button
            variant={statusFiltro === 'TODOS' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setStatusFiltro('TODOS')}
          >
            Todos
          </Button>
          <Button
            variant={statusFiltro === 'PENDENTE' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setStatusFiltro('PENDENTE')}
            sx={{ color: statusFiltro === 'PENDENTE' ? undefined : '#ef6c00' }}
          >
            Pendentes
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadAtividades}
            disabled={loading}
          >
            Atualizar
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Use o Painel de Decisão (fluxo de pagamento) para devolver, rejeitar ou autorizar. Processos em assinatura digital aparecem também na aba Fluxo.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : atividades.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px dashed rgba(21,101,192,0.2)',
            background: 'rgba(247,249,255,0.9)',
            textAlign: 'center'
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} color="primary">
            Nenhum processo de pagamento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Os processos enviados para o fiscal aparecerão aqui.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {atividades.map((atv) => {
            const statusInfo = STATUS_COLORS[atv.status] || { color: '#2e7d32', background: 'rgba(46,125,50,0.12)' };
            const isPendente = atv.status === 'PENDENTE';
            const podeDecidirItem = podeDecidir && isPendente && atv.setor === fiscalSetor;
            return (
              <Paper
                key={atv.id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(21,101,192,0.12)',
                  background: 'rgba(255,193,7,0.06)',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => abrirModal(atv)}
              >
                <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="primary">
                      Processo de Pagamento - {atv.numeroProcesso}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {atv.empresa} · Setor: {atv.setor || '—'}
                      {atv.status === 'EM_ANDAMENTO' ? ' · Aguardando assinatura digital' : ''}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
                    <Chip
                      label={atv.statusLabel}
                      size="small"
                      sx={{ fontWeight: 700, color: statusInfo.color, backgroundColor: statusInfo.background }}
                    />
                    {podeDecidirItem && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DrawOutlinedIcon />}
                        onClick={(e) => { e.stopPropagation(); abrirModal(atv); }}
                      >
                        Painel de Decisão
                      </Button>
                    )}
                    {atv.status === 'AUTORIZADO' && podeEnviarParaFinanceiro && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        startIcon={enviandoFinanceiroId === atv.notaFiscalIdPrincipal ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                        disabled={!!enviandoFinanceiroId}
                        onClick={(e) => handleEnviarParaFinanceiro(atv.notaFiscalIdPrincipal, e)}
                      >
                        {enviandoFinanceiroId === atv.notaFiscalIdPrincipal ? 'Enviando…' : 'Enviar para o Financeiro'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      <PainelDecisaoPagamentoModal
        open={modalOpen}
        onClose={fecharModal}
        atividade={selecionado}
        pdfBlobUrl={pdfBlobUrl}
        loadingPdf={loadingPdf}
        podeDecidir={!!podeDecidirEste}
        enviando={enviando}
        userEmail={user?.email}
        onSubmit={enviarDecisao}
        onContinuarAssinatura={(atv) => {
          if (atv.avaliacaoAssinaturaDocumentoId) {
            navigate(`/assinaturas/documentos/${atv.avaliacaoAssinaturaDocumentoId}`, {
              state: { proximoAssinaturaDocumentoId: atv.assinaturaDocumentoId },
            });
          } else if (atv.assinaturaDocumentoId) {
            navigate(`/assinaturas/documentos/${atv.assinaturaDocumentoId}`);
          }
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
